import React, { useState } from 'react';
import FullscreenDialog from 'Components/UI/FullscreenDialog';
import useLinkModal from 'Components/Modals/LinkModal/useLinkModal';
import { CardActionArea, makeStyles } from '@material-ui/core';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { BulkCommunicationFilterProvider } from 'Components/Modals/BulkCommunicationDialog/BulkCommunicationFilterContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { addQuery, removeQuery } from 'Helpers/Query';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import Typography from 'Components/UI/Typography';
import Card from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Card';
import classNames from 'classnames';
import Button from 'Components/UI/Button';
import fr from 'facet-rest';
import MuiErrorCircleIcon from '@material-ui/icons/Error';
import { Box } from '@facet-wealth/design';
import { useSnackbarContext } from 'Context/SnackbarContext';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: theme.dimensions.xxl,
    alignContent: 'center',
    display: 'flex',
    padding: theme.spacing(5, 10),
  },
  loading: {
    textAlign: 'center',
  },
  previewPane: {
    display: 'flex',
    flexDirection: 'column',
    width: '70%',
    marginRight: theme.spacing(3),
  },
  recipient: {
    minHeight: '74px',
    display: 'flex',
    width: '100%',
    padding: theme.spacing(1),
    cursor: 'pointer',
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.facet.lightGray2,
    },
    justifyContent: 'center',
  },
  sideBar: {
    display: 'flex',
    flexDirection: 'column',
    width: '30%',
  },
  previewActions: {
    width: '100%',
  },
  recipientList: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxHeight: '600px',
    overflowY: 'scroll',
    marginBottom: theme.spacing(2),
  },
  selected: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  cardActionArea: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: '80%',
  },
  previewTitle: {
    marginBottom: theme.spacing(2),
  },
  previewErrors: {
    marginBottom: theme.spacing(4),
  },
  subject: {
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const BulkCommunicationPreviewDialog = () => {
  const classes = useStyles();
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Bulk Communication Preview',
    isPageView: true,
  });
  const [selectedRecipient, setSelectedRecipient] = useState();

  const type = 'bulkCommunicationPreview';
  const link = 'bulkCommunicationPreviewLink';

  const {
    collection,
  } = useLinkModal({
    link,
    type,
    onSubmit: async () => { },
  });

  const handleClose = () => {
    removeQuery(link);
  };

  const resource = collection?.resources().head();

  if (!resource?.attr('recipients')) {
    return null;
  }

  const currentRecipient = selectedRecipient || resource.attr('recipients').resources().head();

  const handleSubmitPreview = async () => {
    try {
      const [createdResource] = await resource.submit();
      removeQuery(link);
      displaySuccess('Submitted bulk communication', 5000);
      if (createdResource?.relationship(fr.relationships.bulkCommunicationForErrorRecipients.name)) {
        addQuery({ bulkCommunicationLink: createdResource.relationship(fr.relationships.bulkCommunicationForErrorRecipients.name).link('related').url() });
      } else {
        removeQuery('modal');
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  return (
    <FullscreenDialog
      disableEnforceFocus // Allows CKEditor to have its own modals
      title="Preview Bulk Communication"
      variant="white"
      test="preview-bulk-communication-modal"
      CloseButtonProps={{
        test: 'preview-bulk-communication-close-modal',
      }}
      onClose={handleClose}
      shouldCloseModal={false}
      hasBackButton
      {...rootNodeProps}
    >
      <BulkCommunicationFilterProvider>
        {!collection ? (
          <div className={classes.loading}>
            <LoadingIndicator />
          </div>
        ) : (
          <div className={classes.root}>
            <div className={classes.previewPane}>
              <Typography className={classes.previewTitle} variant={'h1'} color={'primary'}>{currentRecipient?.attr('name')}</Typography>
              <div className={classes.previewErrors}>
                {currentRecipient?.attr('errors')?.map(error => {
                  return (
                    <Typography color={'error'}>{error}</Typography>
                  );
                })}
              </div>
              {currentRecipient
                ? (
                  <Box>
                    <Typography variant={'h4'} className={classes.subject}>
                      {currentRecipient?.attr('subject')}
                    </Typography>
                    <RichTextDisplay content={currentRecipient?.attr('content')} />
                  </Box>)
                : <Typography>No recipient selected</Typography>}
            </div>
            <div className={classes.sideBar}>
              <div className={classes.recipientList}>
                {resource.attr('recipients').resources().list().map(recipientResource => {
                  const hasErrors = recipientResource.attr('errors')?.length > 0;
                  return (
                    <Card className={classNames(
                      classes.recipient,
                      {
                        [classes.selected]: recipientResource.id() === currentRecipient.id(),
                      },
                    )}
                    >
                      <CardActionArea
                        className={classes.cardActionArea}
                        onClick={() => setSelectedRecipient(recipientResource)}
                      >
                        <div className={classes.cardContent}>
                          <Typography noWrap color={'primary'} className={classes.recipientName}>{recipientResource.attr('name')}</Typography>
                          <Typography variant={'subtitle1'}>[{recipientResource.attr('householdId')}]</Typography>
                        </div>
                        <div>
                          {hasErrors && <MuiErrorCircleIcon color={'error'} />}
                        </div>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </div>
              <div className={classes.previewActions}>
                <Button
                  test={'bulk-communication-send-and-filter'}
                  color="primary"
                  variant={'contained'}
                  onClick={handleSubmitPreview}
                >
                  Send & Filter
                </Button>
              </div>
            </div>
          </div>
        )}
      </BulkCommunicationFilterProvider>
    </FullscreenDialog>
  );
};

BulkCommunicationPreviewDialog.defaultProps = {
};

BulkCommunicationPreviewDialog.propTypes = {
};

export default BulkCommunicationPreviewDialog;
